import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Dialog, Notify} from 'quasar';
import {
	ITicket,
	ITicketSubmit,
	LineItemStatus,
	TLineItemStatus,
	TServiceType,
	IPickupDetails,
	ILineItemOptionChoice,
	ILineItemOption,
	ILineItem,
	CustomerProfileStore,
	TICKET_STATUS,
	TicketsStore
} from '../../store';
import {IBaseResponse, GratuityService, TMaybeGratuityIcon, WebService, ItemService} from '../../services';
import type Vue from 'vue';
import type {DialogChainObject} from 'quasar';
import {VueComponent} from '../../types';
import dayjs from 'dayjs';

export type TTicketResponse = IBaseResponse<{ticket: ITicket}>;
export type TTicketsResponse = IBaseResponse<{tickets: ITicket[]}>;

export type TMaybeTicket = ITicket | null;
export type TTicketId = ITicket['ticketId'];
type TTicketFunction<T> = (arg: TMaybeTicket) => T;
export type TTicketIdFunction<T> = (arg: TTicketId) => T;

const isTicketActive: TTicketFunction<boolean> = (ticket) => {
	const aggregateStatus = TicketsStore.getCleanedTicketLineItemsAggregateStatus(ticket);
	if(typeof aggregateStatus === 'undefined') {
		return false;
	}

	return aggregateStatus < LineItemStatus.DELIVERED;
};


/**
 * model for submitting tickets to the server or for querying submitted tickets
 */
@Module({name: 'tickets_v2', namespaced: true})
class TicketsModule extends VuexModule {
	protected static ACTIVE_TICKET_CLOSE_DELAY = 5; // seconds

	protected static TICKET_STATUSES_CANCELABLE: TLineItemStatus[] = [
		LineItemStatus.PRE_ORDERED,
	];

	/**
	 * stack (LIFO) of active tickets submitted to, or retrieved from, server
	 */
	activeTickets: ITicket[] = [];

	latestTicketInternal: ITicket | null = null;

	hasShownActiveOrderWarning = false;

	skipFeedbackLatestTicketId: string | null = null;

	get activeTicket(): TMaybeTicket {
		// TODO: We'll figure out what "active" means when we support more than one.
		return this.activeTickets?.[0] ?? null;
	}

	// Use this instead of TicketsStore.activeTicket.lineItemsAggregateStatus because
	// this takes into account the ticket status as well.
	get activeTicketLineItemsAggregateStatus() {
		return this.getCleanedTicketLineItemsAggregateStatus(this.activeTicket);
	}

	get latestTicket() {
		return this.latestTicketInternal;
	}

	get shouldShowFeedbackWidget() {
		return !TicketsStore.canShowTicketWidget() &&
			this.latestTicket !== null &&
			this.latestTicket.ticketId !== this.skipFeedbackLatestTicketId &&
			this.latestTicket.closedDate !== null &&
			this.latestTicket.feedback === null &&
			dayjs.unix(this.latestTicket.closedDate).isAfter(dayjs().subtract(7, 'days'));
	}

	get getCleanedTicketLineItemsAggregateStatus() {
		return (ticket: TMaybeTicket): TLineItemStatus => {
			if(ticket) {
				// Cancelling a ticket doesn't update the line item status, so we need to check the ticket status
				// to see if it's cancelled.
				if(ticket.status === TICKET_STATUS.CANCELLED) {
					return LineItemStatus.CANCELLED;
				}

				// If the ticket is closed and has no payments, then it's delivered.
				// If there are payments then it's paid and it's correctly "closed".
				if(ticket.payments.length === 0 && (ticket.status === TICKET_STATUS.CLOSED || ticket.closedDate !== null)) {
					return LineItemStatus.DELIVERED;
				}
				return ticket.lineItemsAggregateStatus;
			}
			return LineItemStatus.PRE_ORDERED;
		};
	}

	get activeTicketsCount(): number {
		return this.activeTickets.filter(
			(ticket) => this.isTicketActive(ticket)
		).length;
	}

	get canSubmitTicket(): boolean {
		// tickets can be submitted only when no other, non-canceled tickets are in the list
		// N.B.: separate method from activeTicketsCount because this logic will change
		return this.activeTicketsCount === 0;
	}

	get gratuityIcon(): TTicketFunction<TMaybeGratuityIcon> {
		return (ticket) => {
			if(ticket === null) {
				return null;
			}

			return GratuityService.determineIcon(ticket.subtotal, ticket.gratuity);
		};
	}

	get isTicketActive(): TTicketFunction<boolean> {
		return isTicketActive;
	}

	get canShowTicketWidget() {
		return (): boolean => {
			return this.activeTicket !== null && this.isTicketActive(this.activeTicket);
		};
	}

	get isTicketCancelable(): TTicketFunction<boolean> {
		return (ticket: TMaybeTicket) => {
			const aggregateStatus = TicketsStore.getCleanedTicketLineItemsAggregateStatus(ticket);
			if(typeof aggregateStatus === 'undefined') {
				return false;
			}
			return TicketsModule.TICKET_STATUSES_CANCELABLE.includes(aggregateStatus);
		};
	}

	get showTicketDetails() {
		return (
			ticketId: string | null,
			parent: InstanceType<typeof Vue>,
			dialogComponent: VueComponent
		): DialogChainObject | null => {
			if(ticketId === null) {
				return null;
			}

			return Dialog.create({
				component: dialogComponent,
				parent,
				ticketId: ticketId,
			});
		};
	}

	get ticketById(): TTicketIdFunction<TMaybeTicket> {
		return (ticketId: TTicketId) => {
			return this.activeTickets.find(
				(ticket) => ticket?.ticketId === ticketId
			) ?? null;
		};
	}

	get ticketDisplayId(): TTicketFunction<string> {
		return (ticket) => {
			return ticket?.ticketId.split('-').pop()?.padStart(4, '0') ?? '';
		};
	}

	get ticketDisplayLineItemOptions() {
		return (options: ILineItem['options'], includeDefaultValues: boolean = false): string[] => {
			const displayOptions: string[] = [];

			options.forEach((option: ILineItemOption) => {
				const displayChoices: string[] = [];

				option.choices.forEach((choice: ILineItemOptionChoice) => {
					if(
						includeDefaultValues ||
						(!includeDefaultValues && choice.isUserSet) ||
						(!includeDefaultValues && option.required) ||
						(!includeDefaultValues && !ItemService.choiceIsSelected(choice) && ItemService.choiceIsIncluded) ||
						(!includeDefaultValues && ItemService.choiceIsSelected(choice) && !ItemService.choiceIsIncluded) ||
						option.alwaysShowOnKds
					) {
						const choiceName = choice.name.trim();

						if(choice.selected) {
							displayChoices.push(choiceName);
						} else if(choice.quantity > 0) {
							displayChoices.push((choice.quantity > 1 ? `${choice.quantity}x ` : '') + choiceName);
						} else if(choice.fractionalQuantity > 0) {
							displayChoices.push((choice.fractionalQuantity > 1 ? `${choice.fractionalQuantity}/${choice.maxFractionalQuantity} ` : '') + choiceName);
						} else if(!choice.selected && choice.included && !option.exclusive && !option.forceHideDeselectedChoices) {
							displayChoices.push(`No ${choiceName}`);
						}
					}
				});

				if(displayChoices.length > 0) {
					displayOptions.push(displayChoices.join(', '));
				}
			});

			return displayOptions;
		};
	}

	get ticketDisplayLineItemOptionsString() {
		return (options: ILineItem['options'], includeDefaultValues: boolean = false): string => {
			return this.ticketDisplayLineItemOptions(options, includeDefaultValues).join(', ');
		};
	}

	get locationIdFromTicketId(): TTicketIdFunction<ITicket['locationId'] | null> {
		return (ticketId) => {
			const localTicket = this.ticketById(ticketId);
			if(localTicket) {
				// This should almost always be the case, and we will need to
				// modify this logic if we start fetching tickets from the server
				// with just the ticketId and no knowledge of the locationId.
				return localTicket.locationId;
			}
			return null;
		};
	}

	get pickupDetails(): IPickupDetails | null {
		return this.activeTicket?.pickupDetails ?? null;
	}

	@Action
	async cancelTicket(ticket: TMaybeTicket): Promise<boolean> {
		const notificationOptions: Parameters<Notify['create']>[0] = {
			message: 'Unable to cancel order.',
			color: 'negative',
			progress: true,
			textColor: 'white',
		};

		if(ticket === null || !this.isTicketCancelable(ticket)) {
			Notify.create(notificationOptions);
			return false;
		}

		try {
			const response = await WebService.getOrderingClient(ticket.locationId).post<TTicketResponse>(
				`/tickets/${ticket.ticketId}/cancel`,
				ticket
			);
			const responseTicket = response?.data?.data?.ticket;
			this.removeTicket(responseTicket);
			notificationOptions.message = 'Order canceled.';
			notificationOptions.color = 'dark';
			Notify.create(notificationOptions);
			try {
				// Refetch the profile so we update the GC balance
				// if they used GC to pay for the order.
				await CustomerProfileStore.fetchProfile();
			} catch(error) {
				// Let's not error on this if it fails for some reason
				console.error(error);
			}
			return true;
		} catch(error: unknown) {
			Notify.create(notificationOptions);
			throw error;
		}
	}

	/**
	 * @deprecated?
	 *
	 * We probably shouldn't be updating ServiceType on a completed ticket.
	 *
	 * @param type
	 */
	@Action
	public async updateServiceTypeId(type: TServiceType) {
		if(!this.activeTicket) {
			throw new Error('No ticket detected');
		}
		const response = await WebService.getOrderingClient(this.activeTicket.locationId).post<IBaseResponse<{ticket: ITicket}>>(`tickets/${this.activeTicket.ticketId}/service_type/update`, {
			type: type
		});
		this.addTicket(response.data.data.ticket);
		return response.data.data.ticket.ticketId;
	}

	@Action
	async updatePickupDetails(params: {ticketId: string; pickupDetails: IPickupDetails}) {
		const locationId = this.locationIdFromTicketId(params.ticketId);
		const result = await WebService.getOrderingClient(locationId).post<TTicketResponse>(`tickets/${params.ticketId}/pickup_details/update`, params.pickupDetails);
		this.addTicket(result.data.data.ticket);
		return result.data.data.ticket.ticketId;
	}

	// TODO: The /tickets endpoint currently does not support customer requests.
	// @Action
	// async fetchTickets(): Promise<boolean> {
	// 	try {
	// 		const response = await WebService.getOrderingClient().post<TTicketsResponse>('/tickets');
	// 	} catch(e) {
	// 	}
	// }

	@Action
	async fetchTicket(ticketId: TTicketId): Promise<ITicket> {
		// This should almost always be available, and we will need to
		// modify this logic if we start fetching tickets from the server
		// with just the ticketId and no knowledge of the locationId.
		const locationId = this.locationIdFromTicketId(ticketId);

		const response = await WebService.getOrderingClient(locationId).get<TTicketResponse>(`/tickets/${ticketId}`);
		const ticket: TMaybeTicket = response?.data?.data?.ticket ?? null;

		if(ticket !== null) {
			if(this.isTicketActive(ticket)) {
				this.addTicket(ticket);
			} else {
				this.removeTicket(ticket);
			}
		}

		return ticket;
	}

	@Action
	async fetchLatestTicket() {
		const response = await WebService.authenticatedClient.get<TTicketsResponse>('tickets', {
			params: {
				count: 1
			}
		});

		const ticket: TMaybeTicket = response?.data?.data?.tickets[0] ?? null;

		if(ticket !== null) {
			if(this.isTicketActive(ticket)) {
				this.addTicket(ticket);
			}
			this.setLatestTicket(ticket);
		}
	}

	@Action
	init(): Promise<void> {
		/**
		 * fetch and update each ticket currently in the store
		 *
		 * Do not edit-in-place (Array.map, etc.) because this isn't a mutation, and to avoid
		 * removing elements from the body being iterated over.
		 *
		 * TODO: we'll replace this with a fetchTickets call to also get tickets that may not be in the local store
		 */
		this.cleanTickets();
		// this.fetchTickets();
		for(const ticket of this.activeTickets) {
			this.fetchTicket(ticket.ticketId).catch(
				() => {
					console.warn(`Failed to reinitialize active ticket ${ticket.ticketId}.`);
					this.removeTicket(ticket);
				}
			);
		}

		/**
		 * @Action wraps this method in a promise, but IDEs and TypeScript don't pick up
		 * on that. In order to use this method in App's initStores' Promise.all() list
		 * without complaint, we want to explicitly (and redundantly) return a promise.
		 * This method SHOULD NOT block initStores (with await) unless strictly necessary.
		 */
		return Promise.resolve();
	}

	@Action
	async submitTicket(ticket: ITicketSubmit): Promise<string> {
		if(!this.canSubmitTicket) {
			throw new Error('Can\'t submit ticket. Too many active tickets.');
		}

		const response = await WebService.getOrderingClient().post<TTicketResponse>(
			'/tickets/submit_app',
			ticket
		);

		const responseTicket = response.data.data.ticket;
		this.addTicket(responseTicket);
		this.setLatestTicket(responseTicket);

		// Update the profile so we get the new points total, but don't let this break anything
		try {
			await CustomerProfileStore.fetchProfile();
		} catch(error) {
			WebService.showError(error);
		}


		return responseTicket.ticketId;
	}

	@Action
	clearTickets() {
		this.activeTickets.forEach((ticket) => this.removeTicket(ticket));
	}

	@Mutation
	cleanTickets() {
		// Israel saw an issue where the activeTickets array had null elements.
		// This is just to clean up any falsey elements.
		this.activeTickets = this.activeTickets.filter((ticket) => {
			return !!ticket;
		});
	}

	@Mutation
	addTicket(ticket: ITicket) {
		// const existingIndex = this.activeTickets.findIndex(t => t.ticketId === ticket?.ticketId);
		// if(existingIndex !== -1) {
		// 	this.activeTickets.splice(existingIndex, 1, ticket);
		// } else {
		// 	this.activeTickets.push(ticket);
		// }

		// TODO: Later we'll want to update the list and the active ticket pointer.
		//       For now, just re-create the list and re-add.
		this.activeTickets = [ticket];

		// Update latest ticket as well if appropriate
		if(
			!this.latestTicketInternal ||
			this.latestTicketInternal.ticketId === ticket.ticketId ||
			(
				this.latestTicketInternal.closedDate &&
				ticket.closedDate &&
				this.latestTicketInternal.closedDate < ticket.closedDate
			)
		) {
			this.latestTicketInternal = ticket;
		}

		// add socket listener
		if(ticket?.websocketUrl && isTicketActive(ticket)) {
			WebService.startWebsockets(ticket.websocketUrl);
		}
	}

	@Mutation
	removeTicket(ticket: ITicket) {
		const existingIndex = this.activeTickets.findIndex(t => t.ticketId === ticket?.ticketId);
		if(existingIndex !== -1) {
			this.activeTickets.splice(existingIndex, 1);
			try {
				WebService.stopWebsockets();
			} catch(error: unknown) {
				const message = error instanceof Error ? error.message : '';
				console.error(`Failed to close websockets for ticket ${ticket?.ticketId}.`, message);
			}
		}
	}

	@Mutation
	setLatestTicket(ticket: ITicket) {
		this.latestTicketInternal = ticket;
	}

	@Mutation
	clearLatestTicket() {
		this.latestTicketInternal = null;
	}

	@Mutation
	setHasShownActiveOrderWarning(value: boolean) {
		this.hasShownActiveOrderWarning = value;
	}

	@Mutation
	setSkipFeedbackLatestTicketId(id: string) {
		this.skipFeedbackLatestTicketId = id;
	}
}

export {
	TicketsModule
};
